import { TableSort, SortHeading, type TableSortValue } from '../components/TableSort';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiCamera, FiDollarSign, FiHeart, FiBookmark, FiCheckCircle, FiPlus, FiFilter, FiList, FiGrid } from 'react-icons/fi';
import { apiRequest } from '../utils/auth';
import { getEffectiveExtensionStatus } from '../utils/extensionStatus';
import { getLensAccessState } from '../utils/lensAccessState';
import ExtensionDetailsModal from '../components/ExtensionDetailsModal';
import CreateExtensionModal from '../components/CreateExtensionModal';
import Pagination from '../components/Pagination';
import TableFilters, { type TableFilterField } from '../components/TableFilters/TableFilters';
import './ExtensionsPage.css';

// Paid tiers, lowest to highest. Mirrors Backend PAID_PLANS.
const PAID_TIER_LADDER = ['basic', 'pro', 'premium'] as const;
type LensTier = 'free' | (typeof PAID_TIER_LADDER)[number];

/**
 * The minimum access tier. Missing tiers follow the API's default Basic access.
 */
function minRequiredTier(availableTiers?: string[]): LensTier {
  if (availableTiers?.includes('free')) return 'free';
  return PAID_TIER_LADDER.find(t => availableTiers?.includes(t)) ?? 'basic';
}

/** Badge naming the minimum plan required to use the extension. */
function AvailabilityBadge({ tiers, style }: { tiers?: string[]; style?: React.CSSProperties }) {
  const tier = minRequiredTier(tiers);
  return (
    <span className={`lens-tier-badge lens-tier-${tier}`} style={style}>
      {tier.charAt(0).toUpperCase() + tier.slice(1)}
    </span>
  );
}


interface Extension {
  _id: string;
  Name: string;
  Creator: string;
  creatorName?: string;
  creatorProfilePicture?: string | null;
  prompt: string;
  CreditCost: number;
  order: number;
  thumbnailImageUrl?: string;
  aiModel?: string;
  official?: boolean;
  availableTiers?: string[];
  requiredPhotoCount?: number;
  categories?: string[];
  aspectRatio?: string;
  sampleImagesCount: number;
  usageCount: number;
  likeCount: number;
  bookmarkCount: number;
  reportStatus?: 'Active' | 'Suspended' | 'inReview';
  isPublished?: boolean;
  isArchived?: boolean;
  isPublic?: boolean;
  isDeleted?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface ModelOption {
  model_id: string;
  display_name?: string;
  enabled?: boolean;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface CreatorResult {
  userId: string;
  name: string;
  email: string;
  avatar?: string;
}

interface SelectedCreator {
  userId: string;
  name: string;
}

interface Filters {
  minLikeCount: string; maxLikeCount: string;
  minBookmarkCount: string; maxBookmarkCount: string;
  minUsageCount: string; maxUsageCount: string;
  minCreditCost: string; maxCreditCost: string;
  isDeleted: string;
  requiredPhotoCount: string;
  aiModels: string[];
  official: string;           // 'all' | 'true' | 'false'
  isPublished: string;        // 'all' | 'true' | 'false'
  isPublic: string;           // 'all' | 'true' | 'false'
  isArchived: string;          // 'all' | 'true' | 'false'
  availability: string[];      // multi-select: 'free' | 'basic' | 'pro' | 'premium' (the minimum tier)
  reportStatuses: string[];    // multi-select: 'Active' | 'Suspended' | 'inReview'
  creators: SelectedCreator[]; // multiple selected creators
}

const defaultFilters: Filters = {
  minLikeCount: '', maxLikeCount: '', minBookmarkCount: '', maxBookmarkCount: '',
  minUsageCount: '', maxUsageCount: '', minCreditCost: '', maxCreditCost: '',
  isDeleted: 'all',
  requiredPhotoCount: '',
  aiModels: [],
  official: 'all',
  isPublished: 'all',
  isPublic: 'all',
  isArchived: 'all',
  availability: [],
  reportStatuses: [],
  creators: [],
};

function countActiveFilters(f: Filters): number {
  let count = Number(f.isDeleted !== 'all') + Number(f.requiredPhotoCount !== '');
  for (const suffix of ['LikeCount', 'BookmarkCount', 'UsageCount', 'CreditCost'] as const) {
    if (f[`min${suffix}`] !== '' || f[`max${suffix}`] !== '') count++;
  }
  if (f.aiModels.length > 0) count++;
  if (f.official !== 'all') count++;
  if (f.isPublished !== 'all') count++;
  if (f.isPublic !== 'all') count++;
  if (f.isArchived !== 'all') count++;
  if (f.availability.length > 0) count++;
  if (f.reportStatuses.length > 0) count++;
  if (f.creators.length > 0) count++;
  return count;
}

function VisibilityBadge({ ext }: { ext: Extension }) {
  const state = getLensAccessState(ext);
  const label = state.charAt(0).toUpperCase() + state.slice(1);
  return (
    <span className={`lens-meta-badge${state === 'public' ? ' lens-meta-public' : ''}`}>{label}</span>
  );
}

function NameBadges({ ext, availability = true, visibility = true }: { ext: Extension; availability?: boolean; visibility?: boolean }) {
  return (
    <>
      {visibility && <VisibilityBadge ext={ext} />}
      {ext.official && <span style={{ fontSize: '12px', color: 'var(--color-accent)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><FiCheckCircle size={14} /> Official</span>}
      {availability && <AvailabilityBadge tiers={ext.availableTiers} />}
    </>
  );
}

type ViewMode = 'list' | 'grid';

const GRID_PAGE_SIZE = 30;

function ExtensionGridCard({ ext, onClick, modelLabel }: { ext: Extension; onClick: () => void; modelLabel: string }) {
  return (
    <div
      onClick={onClick}
      style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', boxSizing: 'border-box', borderRadius: '10px', overflow: 'hidden', display: 'flex', flexDirection: 'column', cursor: 'pointer', opacity: ext.isDeleted ? 0.5 : 1, transition: 'border-color 0.15s' }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--color-accent)')}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--color-border)')}
    >
      {ext.thumbnailImageUrl ? (
        <img src={ext.thumbnailImageUrl} alt={ext.Name} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }} />
      ) : (
        <div style={{ width: '100%', aspectRatio: '1', backgroundColor: 'var(--color-surface-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-muted)' }}>
          <FiCamera size={32} />
        </div>
      )}
      <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontWeight: 600, color: 'var(--color-text)', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', ...(ext.isDeleted ? { textDecoration: 'line-through' } : {}) }}>{ext.Name}</span>
          <NameBadges ext={ext} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          {ext.creatorProfilePicture ? (
            <img src={ext.creatorProfilePicture} alt="" style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
          ) : (
            <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: 'var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-muted)', fontSize: '10px', fontWeight: '600', flexShrink: 0 }}>
              {(ext.creatorName || ext.Creator || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <span style={{ color: 'var(--color-muted)', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ext.creatorName || ext.Creator}</span>
        </div>
        <div style={{ color: 'var(--color-muted)', fontSize: '12px', overflowWrap: 'anywhere' }}>Model: {modelLabel}</div>
        <div style={{ color: 'var(--color-text)', fontSize: '12px' }} title="Successful uses; repeat uses count separately">Usages: {(ext.usageCount ?? 0).toLocaleString()}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ padding: '3px 8px', borderRadius: '4px', backgroundColor: 'var(--color-warning-strong)', color: 'white', fontSize: '12px', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>{ext.CreditCost} <FiDollarSign size={13} /></span>
          <span title="Likes" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: 'var(--color-text)', fontSize: '13px', fontWeight: '600' }}>{ext.likeCount ?? 0} <FiHeart size={13} /></span>
          <span title="Bookmarks" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: 'var(--color-text)', fontSize: '13px', fontWeight: '600' }}>{ext.bookmarkCount ?? 0} <FiBookmark size={13} /></span>
          {(() => {
            const status = getEffectiveExtensionStatus(ext);
            return (
              <span style={{
                padding: '3px 8px', borderRadius: '4px', color: 'white', fontSize: '12px', fontWeight: '500',
                backgroundColor: status.color,
                display: 'inline-flex', alignItems: 'center', gap: '4px',
              }}>
                {status.icon} {status.label}
              </span>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

export default function ExtensionsPage() {
  const navigate = useNavigate();
  // When the URL is /extensions/:extensionId the detail modal is opened for that
  // extension (supports deep-linking / refresh, not just in-app card clicks).
  const { extensionId: routeExtensionId } = useParams<{ extensionId: string }>();
  const [sort, setSort] = useState<TableSortValue>({ field: 'createdAt', direction: 'desc' });
  const changeSort = (value: TableSortValue) => { setCurrentPage(1); setSort(value); };
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [models, setModels] = useState<ModelOption[]>([]);
  const [modelError, setModelError] = useState(false);
  const [modelLoading, setModelLoading] = useState(true);

  const loadModels = useCallback(async () => {
    setModelError(false);
    setModelLoading(true);
    try {
      const response = await apiRequest<{ success: boolean; data: ModelOption[] } | ModelOption[]>('/api/admin/image-models');
      if (!Array.isArray(response) && !response.success) throw new Error('Failed to load image models');
      setModels(Array.isArray(response) ? response : response.data);
    } catch {
      setModelError(true);
    } finally {
      setModelLoading(false);
    }
  }, []);

  useEffect(() => { loadModels(); }, [loadModels]);

  const modelNames = new Map(models.map(model => [model.model_id, model.display_name || model.model_id]));
  const modelLabel = (id?: string) => id ? modelNames.get(id) || id : '—';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedExtension, setSelectedExtension] = useState<Extension | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);

  // View mode (list = paginated table/cards, grid = infinite-scroll thumbnail tiles)
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') return 'grid';
    return (localStorage.getItem('extensionsViewMode') as ViewMode) || 'grid';
  });
  // Grid-view state: infinite scroll (respects the same search + filter panel)
  const [gridExtensions, setGridExtensions] = useState<Extension[]>([]);
  const [gridPage, setGridPage] = useState(1);
  const [gridTotal, setGridTotal] = useState(0);
  const [gridHasMore, setGridHasMore] = useState(false);
  const [gridInitialLoading, setGridInitialLoading] = useState(false);
  const [gridLoadingMore, setGridLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const listRequestId = useRef(0);
  const gridRequestId = useRef(0);

  // On mobile the list/grid toggle is hidden and we always show the grid.
  const effectiveViewMode: ViewMode = isMobile ? 'grid' : viewMode;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    localStorage.setItem('extensionsViewMode', viewMode);
  }, [viewMode]);

  // Filters auto-apply: every change updates this and immediately re-fetches.
  const [appliedFilters, setAppliedFilters] = useState<Filters>(defaultFilters);

  // Creator search
  const [creatorSearch, setCreatorSearch] = useState('');
  const [creatorResults, setCreatorResults] = useState<CreatorResult[]>([]);
  const [creatorSearching, setCreatorSearching] = useState(false);

  const activeFilterCount = countActiveFilters(appliedFilters);

  // Ignore superseded remote searches when typing quickly or clearing the field.
  useEffect(() => {
    let cancelled = false;
    setCreatorResults([]);
    if (creatorSearch.length < 2) {
      setCreatorSearching(false);
      return;
    }
    setCreatorSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await apiRequest<{ success: boolean; data: CreatorResult[] }>(
          `/api/admin/notifications/search-users?q=${encodeURIComponent(creatorSearch)}`
        );
        if (!cancelled && res.success && res.data) setCreatorResults(res.data);
      } catch (err) {
        if (!cancelled) console.error('Failed to search creators', err);
      } finally {
        if (!cancelled) setCreatorSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [creatorSearch]);

  const buildFilterParams = useCallback((f: Filters): Record<string, string> => {
    const params: Record<string, string> = {};
    if (f.aiModels.length > 0) params.aiModel = f.aiModels.join(',');
    if (f.official !== 'all') params.official = f.official;
    if (f.isPublished !== 'all') params.isPublished = f.isPublished;
    if (f.isPublic !== 'all') params.isPublic = f.isPublic;
    if (f.isArchived !== 'all') params.isArchived = f.isArchived;
    if (f.availability.length > 0) params.availability = f.availability.join(',');
    if (f.reportStatuses.length > 0) params.reportStatus = f.reportStatuses.join(',');
    if (f.creators.length > 0) params.creator = f.creators.map(c => c.userId).join(',');
    if (f.isDeleted !== 'all') params.isDeleted = f.isDeleted;
    if (f.requiredPhotoCount) params.requiredPhotoCount = f.requiredPhotoCount;
    for (const suffix of ['LikeCount', 'BookmarkCount', 'UsageCount', 'CreditCost'] as const) {
      for (const prefix of ['min', 'max'] as const) {
        const key = `${prefix}${suffix}` as const;
        if (f[key] !== '') params[key] = f[key];
      }
    }
    return params;
  }, []);

  const fetchExtensions = useCallback(async (page: number = 1, search: string = '', f: Filters = appliedFilters, limit: number = pageSize) => {
    const requestId = ++listRequestId.current;
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        sortBy: sort.field, sortOrder: sort.direction,
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search }),
        ...buildFilterParams(f),
      });

      const response = await apiRequest<{
        success: boolean;
        data: Extension[];
        pagination: PaginationInfo;
        error?: string;
      }>(`/api/admin/extensions?${params}`);

      if (requestId !== listRequestId.current) return;
      if (response.success) {
        setExtensions(response.data);
        setPagination(response.pagination);
      } else {
        setError(response.error || 'Failed to fetch lenses');
      }
    } catch (err) {
      if (requestId !== listRequestId.current) return;
      console.error('Error fetching extensions:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch lenses');
    } finally {
      if (requestId === listRequestId.current) setLoading(false);
    }
  }, [appliedFilters, buildFilterParams, pageSize, sort]);

  useEffect(() => {
    if (effectiveViewMode !== 'list') return;
    fetchExtensions(currentPage, searchQuery, appliedFilters, pageSize);
  }, [effectiveViewMode, currentPage, searchQuery, appliedFilters, pageSize, fetchExtensions]);

  // Grid view fetch: page-by-page, appending for infinite scroll. Respects the
  // same search + filter panel as the list view.
  const fetchGridExtensions = useCallback(async (page: number, replace: boolean) => {
    const requestId = ++gridRequestId.current;
    try {
      if (replace) { setGridInitialLoading(true); setGridLoadingMore(false); setGridHasMore(false); }
      else setGridLoadingMore(true);
      setError(null);
      const params = new URLSearchParams({
        sortBy: sort.field, sortOrder: sort.direction,
        page: page.toString(),
        limit: GRID_PAGE_SIZE.toString(),
        ...(searchQuery && { search: searchQuery }),
        ...buildFilterParams(appliedFilters),
      });
      const response = await apiRequest<{
        success: boolean;
        data: Extension[];
        pagination: PaginationInfo;
        error?: string;
      }>(`/api/admin/extensions?${params}`);
      if (requestId !== gridRequestId.current) return;
      if (response.success) {
        setGridExtensions((prev) => (replace ? response.data : [...prev, ...response.data]));
        setGridPage(response.pagination.page);
        setGridTotal(response.pagination.total);
        setGridHasMore(response.pagination.page < response.pagination.pages);
      } else {
        setError(response.error || 'Failed to fetch lenses');
      }
    } catch (err) {
      if (requestId !== gridRequestId.current) return;
      console.error('Error fetching extensions:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch lenses');
    } finally {
      if (requestId === gridRequestId.current) { setGridInitialLoading(false); setGridLoadingMore(false); }
    }
  }, [appliedFilters, searchQuery, buildFilterParams, sort]);

  // Reset + load first page whenever the grid becomes active or its inputs change
  useEffect(() => {
    if (effectiveViewMode !== 'grid') return;
    fetchGridExtensions(1, true);
  }, [effectiveViewMode, fetchGridExtensions]);

  // Infinite scroll: load the next page when the sentinel scrolls into view
  useEffect(() => {
    if (effectiveViewMode !== 'grid') return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && gridHasMore && !gridLoadingMore && !gridInitialLoading) {
          fetchGridExtensions(gridPage + 1, false);
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [effectiveViewMode, gridHasMore, gridLoadingMore, gridInitialLoading, gridPage, fetchGridExtensions]);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
    const formData = new FormData(e.currentTarget);
    const search = formData.get('search') as string;
    setSearchQuery(search);
  };

  const handleClearFilters = () => {
    setCurrentPage(1);
    setAppliedFilters(defaultFilters);
    setCreatorSearch('');
    setCreatorResults([]);
  };

  const updateFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setCurrentPage(1);
    setAppliedFilters(previous => ({ ...previous, [key]: value }));
  };

  const creatorOptions = [...new Map([
    ...appliedFilters.creators.map(creator => [creator.userId, { value: creator.userId, label: creator.name }] as const),
    ...creatorResults.map(creator => [creator.userId, { value: creator.userId, label: creator.name, description: creator.email }] as const),
  ]).values()];
  const filterFields: TableFilterField[] = [
    ...([['UsageCount', 'Usages'], ['LikeCount', 'Likes'], ['BookmarkCount', 'Bookmarks'], ['CreditCost', 'Cost']] as const).map(([suffix, label]): TableFilterField => {
      const minKey = `min${suffix}` as const; const maxKey = `max${suffix}` as const;
      const minimum = appliedFilters[minKey]; const maximum = appliedFilters[maxKey];
      const invalid = minimum !== '' && maximum !== '' && Number(minimum) > Number(maximum);
      return { id: suffix, label, type: 'custom', active: minimum !== '' || maximum !== '',
        summary: `${minimum || '0'}–${maximum || 'any'}`, onClear: () => { setCurrentPage(1); setAppliedFilters(previous => ({ ...previous, [minKey]: '', [maxKey]: '' })); },
        render: id => <div className="lens-range-field"><div className="lens-count-range">
          <input id={id} className="tf-control" type="number" min="0" step="1" aria-label={`Minimum ${label}`} aria-invalid={invalid} placeholder="Min" value={minimum} onChange={event => updateFilter(minKey, event.target.value)} />
          <span aria-hidden="true">–</span>
          <input className="tf-control" type="number" min="0" step="1" aria-label={`Maximum ${label}`} aria-invalid={invalid} placeholder="Max" value={maximum} onChange={event => updateFilter(maxKey, event.target.value)} />
        </div>{invalid && <small className="lens-range-error">Min must be at most Max.</small>}</div>,
      };
    }),
    { id: 'creators', label: 'Creator', type: 'multi-select', value: appliedFilters.creators.map(creator => creator.userId), options: creatorOptions,
      placeholder: 'All creators', active: appliedFilters.creators.length > 0, onClear: () => updateFilter('creators', []),
      search: creatorSearch, onSearchChange: setCreatorSearch, loading: creatorSearching,
      emptyMessage: creatorSearch.length < 2 ? 'Type at least 2 characters to find creators' : 'No matching creators',
      onChange: ids => updateFilter('creators', ids.map(userId => ({ userId, name: creatorOptions.find(option => option.value === userId)?.label || userId }))) },
    { id: 'aiModels', label: 'Image Models', type: 'multi-select', value: appliedFilters.aiModels,
      options: models.map(model => ({ value: model.model_id, label: model.display_name || model.model_id, description: model.model_id, badge: model.enabled === false ? 'Inactive' : undefined })),
      placeholder: 'All image models', active: appliedFilters.aiModels.length > 0, onClear: () => updateFilter('aiModels', []),
      error: modelError ? 'Failed to load image models.' : undefined, onRetry: loadModels, loading: modelLoading,
      onChange: value => updateFilter('aiModels', value) },
    { id: 'availability', label: 'Availability', type: 'multi-select', value: appliedFilters.availability,
      options: ['free', 'basic', 'pro', 'premium'].map(value => ({ value, label: value.charAt(0).toUpperCase() + value.slice(1) })),
      placeholder: 'All plans', searchable: false, active: appliedFilters.availability.length > 0,
      onClear: () => updateFilter('availability', []), onChange: value => updateFilter('availability', value) },
    { id: 'reportStatuses', label: 'Status', type: 'multi-select', value: appliedFilters.reportStatuses,
      options: [{ value: 'Active', label: 'Active' }, { value: 'Suspended', label: 'Suspended' }, { value: 'inReview', label: 'In Review' }, { value: 'Archived', label: 'Archived' }, { value: 'Deleted', label: 'Deleted' }],
      placeholder: 'All statuses', searchable: false, active: appliedFilters.reportStatuses.length > 0,
      onClear: () => updateFilter('reportStatuses', []), onChange: value => updateFilter('reportStatuses', value) },
    ...([
      ['isDeleted', 'Deleted', 'Deleted only', 'Not deleted'],
      ['official', 'Official', 'Official', 'Community'],
      ['isPublished', 'Published', 'Published', 'Draft'],
      ['isPublic', 'Visibility', 'Public', 'Private'],
      ['isArchived', 'Archived', 'Archived', 'Not archived'],
    ] as const).map(([id, label, yes, no]): TableFilterField => ({ id, label, type: 'select', value: appliedFilters[id],
      options: [{ value: 'all', label: 'All' }, { value: 'true', label: yes }, { value: 'false', label: no }],
      active: appliedFilters[id] !== 'all', onClear: () => updateFilter(id, 'all'), onChange: value => updateFilter(id, value) })),
    { id: 'requiredPhotoCount', label: 'Required photos', type: 'select', value: appliedFilters.requiredPhotoCount,
      options: [{ value: '', label: 'Any' }, ...[1, 2, 3, 4].map(value => ({ value: String(value), label: String(value) }))],
      active: appliedFilters.requiredPhotoCount !== '', onClear: () => updateFilter('requiredPhotoCount', ''), onChange: value => updateFilter('requiredPhotoCount', value) },
  ];

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const refreshCurrentView = () => {
    if (effectiveViewMode === 'grid') fetchGridExtensions(1, true);
    else fetchExtensions(currentPage, searchQuery, appliedFilters, pageSize);
  };

  const openDetailsModal = (ext: Extension) => {
    setSelectedExtension(ext);
    setDetailsModalOpen(true);
    // Reflect the opened extension in the URL (/extensions/:extensionId).
    navigate(`/extensions/${ext._id}`);
  };

  const closeDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedExtension(null);
    navigate('/extensions');
  };

  // Drive the modal from the URL: open (fetching the extension if we don't already
  // have it, e.g. on a fresh page load / refresh of /extensions/:extensionId) or close.
  useEffect(() => {
    if (!routeExtensionId) {
      // Back on /extensions — make sure the modal is closed.
      setDetailsModalOpen(false);
      setSelectedExtension(null);
      return;
    }
    // Already showing this extension (e.g. just opened via a card click) — no refetch.
    if (selectedExtension?._id === routeExtensionId) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await apiRequest<Extension & { success: boolean }>(`/api/admin/extensions/${routeExtensionId}`);
        if (cancelled) return;
        if (res.success && res._id) {
          setSelectedExtension(res);
          setDetailsModalOpen(true);
        } else {
          navigate('/extensions', { replace: true });
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load extension', err);
        navigate('/extensions', { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeExtensionId]);

  if (effectiveViewMode === 'list' && loading && !pagination) {
    return (
      <div className="page-status">Loading lenses...</div>
    );
  }

  return (
    <div className="page">
      <div className="table-page-header">
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Lenses</h1>

        <div className="table-list-toolbar">
          <form onSubmit={handleSearch} className="table-list-search">
            <input
              type="text"
              name="search"
              placeholder="Search lenses..."
              defaultValue={searchQuery}
              style={{
                padding: '8px 12px',
                border: '1px solid var(--color-border)',
                backgroundColor: 'var(--color-input)',
                color: 'var(--color-text)',
                borderRadius: '4px',
                fontSize: '14px',
                flex: 1,
                minWidth: 0,
              }}
            />
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                backgroundColor: 'var(--color-accent-strong)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Search
            </button>
          </form>
          <div className="table-list-actions">
            <TableSort value={sort} onChange={changeSort} loading={effectiveViewMode === 'list' ? loading : gridInitialLoading} options={[
              ['createdAt', 'Created'], ['updatedAt', 'Updated'], ['Name', 'Lens'], ['creatorName', 'Creator'], ['aiModel', 'Model'], ['availability', 'Availability'], ['usageCount', 'Usages'], ['likeCount', 'Likes'], ['bookmarkCount', 'Bookmarks'], ['CreditCost', 'Cost'], ['reportStatus', 'Status'], ['order', 'Display order'],
            ].map(([value, label]) => ({ value, label }))} />
          {!isMobile && (
            <div style={{ display: 'flex', border: '1px solid var(--color-border)', borderRadius: '4px', overflow: 'hidden', flexShrink: 0 }}>
              {(['list', 'grid'] as ViewMode[]).map((mode, idx) => {
                const active = viewMode === mode;
                return (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    title={mode === 'list' ? 'List view' : 'Grid view'}
                    style={{
                      padding: '8px 12px',
                      border: 'none',
                      borderLeft: idx === 0 ? 'none' : '1px solid var(--color-border)',
                      backgroundColor: active ? 'var(--color-accent-strong)' : 'var(--color-surface-hover)',
                      color: active ? 'white' : 'var(--color-muted)',
                      cursor: 'pointer',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    {mode === 'list' ? <FiList size={15} /> : <FiGrid size={15} />}
                    {mode === 'list' ? 'List' : 'Grid'}
                  </button>
                );
              })}
            </div>
          )}
          <button
            onClick={() => setShowFilters(prev => !prev)}
            style={{
              padding: '8px 16px',
              backgroundColor: showFilters ? 'var(--color-accent-strong)' : 'var(--color-surface-hover)',
              color: showFilters ? 'white' : 'var(--color-text)',
              border: '1px solid var(--color-border)',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <FiFilter size={14} /> Filter
            {activeFilterCount > 0 && (
              <span style={{
                backgroundColor: 'var(--color-accent-strong)',
                color: 'white',
                borderRadius: '50%',
                width: '18px',
                height: '18px',
                fontSize: '11px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600',
              }}>
                {activeFilterCount}
              </span>
            )}
          </button>
          <button className="table-create-action"
            onClick={() => setCreateModalOpen(true)}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--color-success-strong)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <FiPlus size={16} /> Create
          </button>
          </div>
        </div>
      </div>


      {showFilters && <TableFilters label="Lens filters" fields={filterFields} onClearAll={handleClearFilters} />}

      {error && (
        <div style={{
          padding: '12px',
          backgroundColor: 'var(--color-danger-surface)',
          color: 'var(--color-danger)',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
          <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {effectiveViewMode === 'list' && pagination && (
        <div style={{ marginBottom: '16px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ color: 'var(--color-muted)' }}>Showing {extensions.length} of {pagination.total} extensions</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--color-muted)' }}>
            <label htmlFor="ext-page-size">Per page:</label>
            <select
              id="ext-page-size"
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              style={{ padding: '4px 8px', border: '1px solid var(--color-border)', borderRadius: '4px', backgroundColor: 'var(--color-input)', color: 'var(--color-text)', fontSize: '13px' }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </span>
        </div>
      )}

      {effectiveViewMode === 'list' && (
      <div className="lens-table-scroll" role="region" aria-label="Lenses table" tabIndex={0}>
        <table className="lens-table" aria-busy={loading}>
          <thead><tr>
            {([
              ['Name', 'Lens'], [null, 'Visibility'], ['creatorName', 'Creator'], ['aiModel', 'Model'], ['availability', 'Availability'],
              ['usageCount', 'Usages'], ['likeCount', 'Likes'], ['bookmarkCount', 'Bookmarks'], ['CreditCost', 'Cost'], ['reportStatus', 'Status'],
            ] as const).map(([field, label]) => <th key={label} scope="col"
              className={field && ['CreditCost', 'usageCount', 'likeCount', 'bookmarkCount'].includes(field) ? 'lens-number' : undefined}
              aria-sort={field ? (sort.field === field ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}>
              {field ? <SortHeading field={field} label={label} value={sort} onChange={changeSort} /> : label}
            </th>)}
          </tr></thead>
          <tbody>
            {extensions.map(ext => {
              const status = getEffectiveExtensionStatus(ext);
              return <tr key={ext._id} className={ext.isDeleted ? 'lens-row-deleted' : undefined} onClick={() => openDetailsModal(ext)}>
                <td>
                  <div className="lens-identity">
                    {ext.thumbnailImageUrl ? <img className="lens-thumbnail" src={ext.thumbnailImageUrl} alt="" loading="lazy" />
                      : <div className="lens-thumbnail lens-thumbnail-empty"><FiCamera size={22} /></div>}
                    <div className="lens-identity-copy">
                      <button type="button" className="lens-row-open" onClick={event => { event.stopPropagation(); openDetailsModal(ext); }}>{ext.Name}</button>
                      <div className="lens-name-badges"><NameBadges ext={ext} availability={false} visibility={false} /></div>
                    </div>
                  </div>
                </td>
                <td><div className="lens-visibility-badges"><VisibilityBadge ext={ext} /></div></td>
                <td><div className="lens-creator">
                  {ext.creatorProfilePicture ? <img src={ext.creatorProfilePicture} alt="" loading="lazy" />
                    : <span className="lens-creator-avatar">{(ext.creatorName || ext.Creator || '?').charAt(0).toUpperCase()}</span>}
                  <span title={ext.creatorName || ext.Creator}>{ext.creatorName || ext.Creator}</span>
                </div></td>
                <td className="lens-model" title={ext.aiModel}>{modelLabel(ext.aiModel)}</td>
                <td><AvailabilityBadge tiers={ext.availableTiers} /></td>
                <td className="lens-number">{(ext.usageCount ?? 0).toLocaleString()}</td>
                <td className="lens-number">{(ext.likeCount ?? 0).toLocaleString()}</td>
                <td className="lens-number">{(ext.bookmarkCount ?? 0).toLocaleString()}</td>
                <td className="lens-number"><span className="lens-cost-badge" title="Credits per use">
                  {(ext.CreditCost ?? 0).toLocaleString()}<FiDollarSign size={13} aria-hidden="true" />
                </span></td>
                <td><span className={`lens-status lens-status-${status.key}`}>{status.icon}{status.label}</span></td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>
      )}

      {effectiveViewMode === 'list' && pagination && (
        <Pagination
          currentPage={currentPage}
          totalPages={pagination.pages}
          onPageChange={handlePageChange}
        />
      )}

      {/* ===== GRID VIEW ===== */}
      {effectiveViewMode === 'grid' && (
        <>
          {gridTotal > 0 && !gridInitialLoading && (
            <div style={{ marginBottom: '16px', fontSize: '14px', color: 'var(--color-muted)' }}>
              Showing {gridExtensions.length} of {gridTotal.toLocaleString()} extensions
            </div>
          )}
          {gridInitialLoading ? (
            <div style={{ padding: '48px', textAlign: 'center', color: 'var(--color-muted)' }}>Loading lenses…</div>
          ) : gridExtensions.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center', color: 'var(--color-muted)' }}>No lenses found.</div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '14px' }}>
                {gridExtensions.map((ext) => (
                  <ExtensionGridCard key={ext._id} ext={ext} modelLabel={modelLabel(ext.aiModel)} onClick={() => openDetailsModal(ext)} />
                ))}
              </div>
              {/* Infinite scroll sentinel + loading indicator */}
              <div ref={sentinelRef} style={{ height: '1px' }} />
              {gridLoadingMore && (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-muted)', fontSize: '14px' }}>Loading more…</div>
              )}
              {!gridHasMore && !gridLoadingMore && (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-subtle)', fontSize: '13px' }}>End of results</div>
              )}
            </>
          )}
        </>
      )}

      {/* Extension Details Modal */}
      {selectedExtension && (
        <ExtensionDetailsModal
          extension={selectedExtension}
          isOpen={detailsModalOpen}
          onClose={closeDetailsModal}
          onExtensionUpdated={(updated) => {
            setExtensions(prev => prev.map(e => e._id === updated._id ? { ...e, ...updated } : e));
            setGridExtensions(prev => prev.map(e => e._id === updated._id ? { ...e, ...updated } : e));
            setSelectedExtension(prev => prev ? { ...prev, ...updated } : prev);
          }}
          onStatusChanged={refreshCurrentView}
          onExtensionDeleted={(deletedId) => {
            setExtensions(prev => prev.filter(e => e._id !== deletedId));
            setGridExtensions(prev => prev.filter(e => e._id !== deletedId));
            closeDetailsModal();
          }}
        />
      )}

      {/* Create Extension Modal */}
      <CreateExtensionModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={refreshCurrentView}
      />
    </div>
  );
}
